/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Введение</h2>
    <p className={styles.description}>
      В JavaScript функции не отличаются от чисел, строк или массивов - это просто специальный тип данных (объект),
      который можно хранить в переменной или передавать как аргумент в другую функцию.
    </p>
    <p className={styles.description}>
      <b>Функция высшего порядка</b> — функция, принимающая в качестве параметров другие функции или возвращающая
      функцию как результат.
    </p>
    <p className={styles.description}>
      <b>Функция обратного вызова (callback)</b> — это функция, которая передается другой функции в качестве аргумента и
      та, в свою очередь, вызывает переданную функцию.
    </p>
    <CopyBlock
      text={`const printMessage = function (message) {
  console.log(message);
};

const higherOrderFunction = function (callback) {
  const string = 'HOCs are awesome';
  callback(string);
};

higherOrderFunction(printMessage);
`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Функции обратного вызова применяются для обработки действий пользователя на странице, при вызове функций к
      определенному времени, при обработке запросов на сервер и др.
    </p>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Абстрагирование повторения</h2>
      <p className={styles.description}>
        <b>Абстракция</b> — скрытие деталей реализации. Позволяет думать о задачах на более высоком (абстрактном)
        уровне.
      </p>
      <p className={styles.description}>
        Функции - это хороший способ построения абстракций. Допустим, скрипт выполняет какое-то действие определенное
        количество раз. Для этого можно написать цикл
        <code className={styles.code}>for</code>.
      </p>
      <CopyBlock
        text={`for (let i = 0; i < 10; i += 1) {
  console.log(i);
}
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Можем ли мы абстрагировать «делать что-то N раз» как функцию? - да, напишем функцию, которая вызывает{' '}
        <code className={styles.code}>console.log()</code> N раз.
      </p>
      <CopyBlock
        text={`const repeatLog = function (n) {
  for (let i = 0; i < n; i += 1) {
    console.log(i);
  }
};

repeatLog(5);
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Но что, если мы хотим сделать что-то, кроме логирования чисел? Поскольку «делать что-то» можно представить как
        функцию, а функции - это просто значения, мы можем передать действие как аргумент.
      </p>
      <CopyBlock
        text={`const printValue = function (value) {
  console.log(value);
};

const prettyPrint = function (value) {
  console.log('Logging value: ', value);
};

const repeat = function (n, action) {
  for (let i = 0; i < n; i += 1) {
    action(i);
  }
};

// Передаем printValue как callback-функцию
repeat(3, printValue);
// 0
// 1
// 2

// Передаем prettyPrint как callback-функцию
repeat(3, prettyPrint);
// Logging value: 0
// Logging value: 1
// Logging value: 2
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Часто callback-функции анонимны и объявляются как стрелочные функции прямо во время передачи.
      </p>
      <CopyBlock
        text={`const repeat = function (n, action) {
  for (let i = 0; i < n; i += 1) {
    action(i);
  }
};

const labels = [];

repeat(5, value => {
  labels.push(\`Label \${value + 1}\`);
});

console.log(labels); // ["Label 1", "Label 2", "Label 3", "Label 4", "Label 5"]
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - Фильтрация массива</h2>
      <p className={styles.description}>
        Напишем функцию фильтрации элементов массива. Первый параметр <code className={styles.code}>array</code> - это
        массив, будем перебирать его используя цикл <code className={styles.code}>for...of</code>.
      </p>
      <CopyBlock
        text={`const filter = function (array) {
  for (const element of array) {
  }
};`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Второй параметр <code className={styles.code}>test</code> - это callback-функция, которую будет необходимо
        выполнить для каждого элемента массива.
      </p>
      <CopyBlock
        text={`const filter = function (array, test) {
  for (const element of array) {
    test(element);
  }
};`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Функция <code className={styles.code}>filter</code> создает новый массив, в который будет добавляться результат
        выполнения callback-функции <code className={styles.code}>test</code> над каждым элементом массива{' '}
        <code className={styles.code}>array</code>. Результатом работы функции будет массив отфильтрованных элементов.
      </p>
      <CopyBlock
        text={`const filter = function (array, test) {
  const filteredElements = [];

  for (const element of array) {
    test(element);
  }

  return filteredElements;
};`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если элемент массива проходит тест, то есть callback-функция возвращает{' '}
        <code className={styles.code}>true</code>, он добавляться в отфильтрованный массив.
      </p>
      <CopyBlock
        text={`const filter = function (array, test) {
  const filteredElements = [];

  for (const element of array) {
    const passed = test(element);

    if (passed) {
      filteredElements.push(element);
    }
  }

  return filteredElements;
};`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.notificationStudy}>
        Обратите внимание, функция <code className={styles.codeError}>filter</code>, вместо удаления элементов из
        оригинального массива, создает новый массив только тех элементов, которые проходят тест. Это - чистая функция,
        она не изменяет исходные данные.
      </p>
      <p className={styles.description}>
        При таком подходе мы можем выполнять фильтрацию массива по критерию используя всего одну функцию для перебора, а
        операции над каждым элементом будут зависеть от callback-функции.
      </p>
      <CopyBlock
        text={`const filter = function (array, test) {
  const filteredElements = [];

  for (const element of array) {
    const passed = test(element);

    if (passed) {
      filteredElements.push(element);
    }
  }

  return filteredElements;
};

const fruits = [
  { name: 'apples', quantity: 200, isFresh: true },
  { name: 'grapes', quantity: 150, isFresh: false },
  { name: 'bananas', quantity: 100, isFresh: true },
];

const freshFruits = filter(fruits, fruit => fruit.isFresh);
console.log(freshFruits); // массив с объектами apples и bananas

const fruitsWithQuantity = filter(fruits, fruit => fruit.quantity >= 120);
console.log(fruitsWithAmount); // массив с объектами apples и grapes
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
